/* eslint-disable camelcase */
import React from 'react'
import toast from 'react-hot-toast'

import MemoClose from '../Icons/Close'

import { Box, IconButton } from '../../../components'

import { useTheme } from '../../../hooks/useTheme'
import { useNotificationsHomeContext } from '../../context'

import NotificationsList from '../NotificationsList'
import { hexToRGBA } from '../../helpers/hextoRGBA'
import { useBreakpoints } from '../../../hooks/useBreakpoints'

import { useNotificationCenterStyles } from '../../../hooks/styles/useNotificationCenterStyles'

import { ToastNotificationContent } from '../InAppNotification/ToastNotificationContent'
import { HeaderActions } from './HeaderActions'
import { LabelFilter } from './LabelFilter'

export const ToastStructure = ({ msg, t, socketInstance, logo, close }) => {
  const handleMarkAsRead = (item) => {
    socketInstance?.emit('message:read', item)
  }

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation()
        toast.dismiss(t.id)
      }}
      className='fyno-toast'
      style={{
        textDecoration: 'none',
        width: '100%',
        position: 'relative'
      }}
    >
      <ToastNotificationContent
        item={msg}
        handleMarkAsRead={handleMarkAsRead}
        logo={logo}
      />

      {/* Close button overlay */}
      {close && (
        <IconButton
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toast.dismiss(t.id)
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: 'rgba(58, 53, 65, 0.04)',
            zIndex: 10
          }}
          size='small'
        >
          <MemoClose fontSize='small' />
        </IconButton>
      )}
    </Box>
  )
}

export const NotificationCenterBody = ({ sx = {} }) => {
  const {
    data: { tabPanelValue, unreadList, selectedLabel },
    handlers: { handleChangeTabs, setAnchorDeleteEl }
  } = useNotificationsHomeContext()

  const { NCHeaderStyles } = useNotificationCenterStyles()

  const theme = useTheme()

  const { smUp } = useBreakpoints()

  const modifiedUnreadList =
    unreadList?.filter((item) => {
      if (!selectedLabel) {
        return true
      }

      return item?.notification_content?.label === selectedLabel
    }) || []

  // Get the chip color from sx prop or fallback to theme primary
  const chipColor = sx.chipColor || theme.palette.primary?.main
  const chipBackgroundColor =
    sx.chipBackgroundColor || hexToRGBA(chipColor, 0.12)

  const handleTabClick = (tabValue) => {
    handleChangeTabs(null, tabValue)
    setAnchorDeleteEl(false)
  }

  const tabStyles = {
    padding: smUp ? '10px 20px' : '8px 18px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: smUp ? '14px' : '12px',
    fontWeight: 500,
    color: theme.palette.background?.inverted,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    outline: 'none'
  }

  const activeTabStyles = {
    ...tabStyles,
    color: theme.palette.primary?.main || '#ffffff',
    backgroundColor: hexToRGBA(theme.palette.primary?.main, 0.12)
  }

  const getChipStyles = (isActive) => ({
    backgroundColor: isActive
      ? chipBackgroundColor || '#ffffff'
      : chipBackgroundColor,
    color: isActive
      ? theme.palette.primary?.main
      : theme.palette.background?.inverted,
    fontSize: '12px',
    fontWeight: '600',
    padding: '2px 5px',
    borderRadius: '12px',
    minWidth: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none'
  })

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        ...sx
      }}
    >
      <NCHeaderStyles>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            margin: '5px 0 5px 5px'
          }}
          data-testid='notification-center-tabs'
        >
          <button
            style={tabPanelValue === 'all' ? activeTabStyles : tabStyles}
            onClick={() => handleTabClick('all')}
          >
            ALL
          </button>
          <button
            style={tabPanelValue === 'unread' ? activeTabStyles : tabStyles}
            onClick={() => handleTabClick('unread')}
          >
            UNREAD
            {modifiedUnreadList?.length >= 0 && (
              <span style={getChipStyles(tabPanelValue === 'unread')}>
                {modifiedUnreadList?.length}
              </span>
            )}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.1rem' }}>
          <LabelFilter />
          <HeaderActions />
        </div>
      </NCHeaderStyles>

      {/* Tab Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {tabPanelValue === 'all' && <NotificationsList filter={false} />}
        {tabPanelValue === 'unread' && <NotificationsList filter />}
      </div>
    </div>
  )
}

export default NotificationCenterBody
